import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IonPage } from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Logo } from '@/components/ui/Logo';
import { AnnotationPanel } from '@/components/annotations/AnnotationPanel';
import type { AnnotationFilter } from '@/components/annotations/AnnotationPanel';
import { AnnotationCompose } from '@/components/annotations/AnnotationCompose';
import { SelectionToolbar } from '@/components/selection/SelectionToolbar';
import { MarginNoteRail } from '@/components/reader/MarginNoteRail';
import { ReaderSettings } from '@/components/reader/ReaderSettings';
import { useAuth } from '@/lib/auth/useAuth';
import { getDocument, trackDocumentView } from '@/lib/api/documents';
import { getPublishedVersion } from '@/lib/api/versions';
import { createAnnotation, getAnnotationsByVersion } from '@/lib/api/annotations';
import type { DisplayAnnotation } from '@/lib/api/annotations';
import { captureSelection } from '@/lib/annotations/selectors';
import type { Selector } from '@/lib/annotations/selectors';
import type { ReadMode, ReaderPrefs } from './reader/types';
import { DEFAULT_PREFS, themeBg, themeBorder, themeMuted, themeText, fontFamilyValue } from './reader/types';

type Annotation = DisplayAnnotation;

// ── Mock content (shown when no document ID is in the route) ──────────────────

const BOOK_PAGES = [
  `Ralph Waldo Emerson was born in Boston, May 25, 1803. He was descended from a long line of New England ministers, men of refinement and education. As a school-boy he was quiet and retiring, reading a great deal, but not paying much attention to his lessons. He entered Harvard at the early age of fourteen, but never attained a high rank there, although he took a prize for an essay on Socrates, and was made class poet after several others had declined.

Next to his reserve and the faultless propriety of his conduct, his contemporaries at college seemed most impressed by the great maturity of his mind. Emerson appears never to have been really a boy. He was always serene and thoughtful, impressing all who knew him with that spirituality which was his most distinguishing characteristic.

After graduating from college he taught school for a time, and then entered the Harvard Divinity School under Dr. Channing, the great Unitarian preacher. Although he was not strong enough to attend all the lectures of the divinity course, the college authorities deemed the name Emerson sufficient passport to the ministry.`,

  `He was accordingly "approbated to preach" by the Middlesex Association of Ministers on October 10, 1826. As a preacher, Emerson was interesting, though not particularly original. His talent seems to have been in giving new meaning to the old truths of religion.

One of his hearers has said: "In looking back on his preaching I find he has impressed truths to which I always assented in such a manner as to make them appear new, like a clearer revelation." Although his sermons were always couched in scriptural language, they were touched with the light of that genius which avoids the conventional and commonplace.

In his other pastoral duties Emerson was not quite so successful. It is characteristic of his deep humanity and his dislike for all fuss and commonplace that he appeared to least advantage at a funeral. A connoisseur in such matters, an old sexton, once remarked that on such occasions "he did not appear at ease at all. To tell the truth, in my opinion, that young man was not born to be a minister."`,

  `Emerson did not long remain a minister. In 1832 he preached a sermon in which he announced certain views in regard to the communion service which were disapproved by a large part of his congregation. He found it impossible to continue preaching what he could not fully believe in, and from both sides, he parted from his congregation.

A few months later (1833) he went to Europe for a short year of travel. While abroad, he visited Walter Savage Landor, Coleridge and Wordsworth, and Thomas Carlyle. This visit to Carlyle was to both men a most interesting experience. They parted feeling that they had much intellectually in common.

This belief fostered a sympathy which, by the time they had discovered how different they really were, had grown so strong a habit that they always kept up their intimacy. This year of travel opened Emerson's eyes to many things of which he had previously been ignorant; he had profited by detachment from the concerns of a limited community and an isolated church.`,

  `After his return he began to find his true field of activity in the lecture-hall, and delivered a number of addresses in Boston and its vicinity. While thus coming before the open public on the lecture platform, he was all the time preparing the treatise which was to embody all the quintessential elements of his philosophical doctrine. This was the essay Nature, which was published in 1836.

By its conception of external Nature as an incarnation of the Divine Mind it struck the fundamental principle of Emerson's religious belief. By it he drew around him a circle of the most brilliant men that America has yet produced — men who were called, though they never formed a real organization, the Transcendental Club.

Among these were Bronson Alcott, George Ripley, Theodore Parker, Henry David Thoreau, Margaret Fuller, and other famous names. These men were united by a common tendency toward Idealism, and a belief in the primacy of spirit over matter.`,
];

const MOCK_SELECTOR: Selector = { position: { start: 0, end: 0 }, quote: { exact: '', prefix: '', suffix: '' } };

const INITIAL_ANNOTATIONS: Annotation[] = [
  {
    id: 'a1',
    selector: { ...MOCK_SELECTOR, quote: { exact: 'quiet and retiring, reading a great deal, but not paying much attention to his lessons', prefix: '', suffix: '' } },
    excerpt: 'quiet and retiring, reading a great deal, but not paying much attention to his lessons',
    bodyMd: 'This mirrors so many great thinkers — inner absorption over institutional performance. The "quiet rebel" archetype.',
    authorHandle: 'sarah-m', authorName: 'Sarah M.', initials: 'SM', color: '#2563eb',
    createdAt: new Date(Date.now() - 2 * 86400_000).toISOString(), isPublic: true,
  },
  {
    id: 'a2',
    selector: { ...MOCK_SELECTOR, quote: { exact: 'Emerson appears never to have been really a boy', prefix: '', suffix: '' } },
    excerpt: 'Emerson appears never to have been really a boy',
    bodyMd: 'Spiritual precocity — he seems to arrive already formed. What does this say about the relationship between childhood and genius?',
    authorHandle: 'prof-chen', authorName: 'Prof. Chen', initials: 'PC', color: '#7c3aed',
    createdAt: new Date(Date.now() - 7 * 86400_000).toISOString(), isPublic: true,
  },
  {
    id: 'a3',
    selector: { ...MOCK_SELECTOR, quote: { exact: '"approbated to preach" by the Middlesex Association', prefix: '', suffix: '' } },
    excerpt: '"approbated to preach" by the Middlesex Association',
    bodyMd: 'Private: verify exact date before citing — Oct 10, 1826 per this source. Cross-check with Rusk biography.',
    authorHandle: 'you', authorName: 'You', initials: 'ME', color: '#059669',
    createdAt: new Date(Date.now() - 86400_000).toISOString(), isPublic: false,
  },
];

// ── TipTap content extraction ─────────────────────────────────────────────────

type TipTapNode = { type: string; text?: string; content?: TipTapNode[] };

function extractParagraphs(node: TipTapNode): string[] {
  if (!node) return [];
  if (node.type === 'text') return [];
  const blockTypes = new Set(['paragraph', 'heading', 'blockquote', 'codeBlock', 'listItem']);
  const paras: string[] = [];

  function textOf(n: TipTapNode): string {
    if (!n) return '';
    if (n.type === 'text') return n.text ?? '';
    if (n.type === 'hardBreak') return '\n';
    return (n.content ?? []).map(textOf).join('');
  }

  function walk(n: TipTapNode) {
    if (blockTypes.has(n.type)) {
      const t = textOf(n).trim();
      if (t) paras.push(t);
    } else if (n.content) {
      n.content.forEach(walk);
    }
  }

  (node.content ?? []).forEach(walk);
  return paras;
}

// Strip style/script blocks (they pollute global CSS — e.g. `clear:both` on headings breaks
// CSS column layout, and `body { margin-left: 10% }` shifts the whole page) plus inline
// event handlers from the old CF JS runtime.
//
// Some legacy books (e.g. Walden) use JS-driven TOC navigation:
//   <a href="#one" onClick="gototocchapter(N);">Title</a>
// The real chapter anchors are id/name="cN" or "WN" in document order. We rewrite the
// dummy href to the correct anchor before stripping onClick, so paged navigation works.
function sanitizeLegacyHtml(html: string): string {
  // Collect chapter-like anchor IDs in document order (cN, WN patterns).
  // Walden uses both W9 and c9 for the same chapter — deduplicate by numeric suffix so
  // the 28-entry raw list collapses to the correct 19 entries matching gototocchapter(N).
  const chapterAnchors: string[] = [];
  const seenNums = new Set<number>();
  const anchorIdRe = /(?:id|name)="((?:c|W)\d+)"/gi;
  let m: RegExpExecArray | null;
  while ((m = anchorIdRe.exec(html)) !== null) {
    const id = m[1];
    const num = parseInt(id.replace(/\D+/g, ''), 10);
    if (!isNaN(num) && !seenNums.has(num)) { seenNums.add(num); chapterAnchors.push(id); }
  }

  if (chapterAnchors.length > 0) {
    html = html.replace(/<a\s([^>]*)>/gi, (fullMatch, attrs: string) => {
      if (!attrs.includes('gototocchapter')) return fullMatch;
      const numMatch = /gototocchapter\((\d+)\)/.exec(attrs);
      if (!numMatch) return fullMatch;
      const idx = parseInt(numMatch[1], 10) - 1;
      if (idx < 0 || idx >= chapterAnchors.length) return fullMatch;
      const newAttrs = attrs.replace(/href="[^"]*"/, `href="#${chapterAnchors[idx]}"`);
      return `<a ${newAttrs}>`;
    });
  }

  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\s+on\w+="[^"]*"/gi, '');
}

function scrollToAnchor(anchorId: string) {
  const el = document.getElementById(anchorId) ?? document.querySelector(`[name="${anchorId}"]`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

interface SelectionState {
  text: string;
  rect: DOMRect;
}

function renderPageContent(
  pageText: string,
  annotations: DisplayAnnotation[],
  onAnnClick: (ann: DisplayAnnotation) => void,
  activeId: string | null,
) {
  const paragraphs = pageText.split('\n\n');
  return paragraphs.map((para, i) => (
    <p key={i} style={{ marginBottom: '1.2em' }}>
      {renderHighlightedParagraph(para, annotations, onAnnClick, activeId)}
    </p>
  ));
}

function renderHighlightedParagraph(
  text: string,
  annotations: DisplayAnnotation[],
  onAnnClick: (ann: DisplayAnnotation) => void,
  activeId: string | null,
) {
  const spans = annotations
    .map(ann => ({ start: text.indexOf(ann.excerpt), end: text.indexOf(ann.excerpt) + ann.excerpt.length, ann }))
    .filter(s => s.start !== -1)
    .sort((a, b) => a.start - b.start);

  const nodes: React.ReactNode[] = [];
  let pos = 0;
  spans.forEach(({ start, end, ann }) => {
    if (start > pos) nodes.push(text.slice(pos, start));
    nodes.push(
      <mark
        key={ann.id}
        onClick={e => { e.stopPropagation(); onAnnClick(ann); }}
        style={{
          background: activeId === ann.id ? 'rgba(251,191,36,0.95)' : 'rgba(254,240,138,0.72)',
          borderBottom: `2px solid ${ann.color}`,
          borderRadius: '2px',
          cursor: 'pointer',
          padding: '0 1px',
          transition: 'background 0.2s',
        }}
      >
        {ann.excerpt}
      </mark>,
    );
    pos = end;
  });
  if (pos < text.length) nodes.push(text.slice(pos));
  return nodes;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ReaderPage() {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const { status } = useAuth();
  const queryClient = useQueryClient();

  const pageKey = id ? `oipub-page-${id}` : 'oipub-page';

  // ── UI state ────────────────────────────────────────────────────────────
  const [readMode, setReadMode] = useState<ReadMode>(() => (localStorage.getItem('oipub-mode') as ReadMode) ?? 'paged');
  const [showAnnPanel, setShowAnnPanel] = useState(false);
  const [activeAnn, setActiveAnn] = useState<string | null>(null);
  const [annFilter, setAnnFilter] = useState<AnnotationFilter>('all');
  const [bookmarked, setBookmarked] = useState(false);
  const [selection, setSelection] = useState<SelectionState | null>(null);
  const [capturedSelector, setCapturedSelector] = useState<Selector | null>(null);
  const [composing, setComposing] = useState(false);
  const [mockAnnotations, setMockAnnotations] = useState<Annotation[]>(INITIAL_ANNOTATIONS);
  const [prefs, setPrefs] = useState<ReaderPrefs>(() => {
    try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem('oipub-prefs') ?? '{}') }; }
    catch { return DEFAULT_PREFS; }
  });
  const [showSettings, setShowSettings] = useState(false);

  // ── Paging state ────────────────────────────────────────────────────────
  const [spreadOffset, setSpreadOffset] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sliderPage, setSliderPage] = useState(0);
  const [sliding, setSliding] = useState(false);
  const [contentWidth, setContentWidth] = useState(1000);
  const [contentHeight, setContentHeight] = useState(600);
  // Incremented each time all images in the current content finish loading.
  // Adding it to the useLayoutEffect deps causes the anchor map to be rebuilt
  // after images reflow the columns — critical for image-heavy documents.
  const [imagesLoadedCount, setImagesLoadedCount] = useState(0);
  // True while images in the current content are still loading — navigation is
  // blocked until layout stabilises so page count and anchor map are accurate.
  const [imagesLoading, setImagesLoading] = useState(false);

  const textRef = useRef<HTMLDivElement>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const touchX = useRef<number | null>(null);
  const spreadStepRef = useRef<number>(0);
  // Kept in sync with totalPages so the keyboard handler can read it without being a dep.
  const totalPagesRef = useRef<number>(1);
  // Pre-computed anchor-id → spread-index. Built once per layout in useLayoutEffect by
  // batch-reading all anchor rects while layout is already resolved (no extra reflow).
  const anchorSpreadMapRef = useRef<Map<string, number>>(new Map());

  // ── Real-data queries ────────────────────────────────────────────────────
  const { data: docInfo } = useQuery({
    queryKey: ['document', id],
    queryFn: () => getDocument(id!),
    enabled: !!id,
  });

  const { data: publishedVersion, isLoading: versionLoading } = useQuery({
    queryKey: ['published-version', id],
    queryFn: () => getPublishedVersion(id!),
    enabled: !!id,
  });

  const { data: annotationData } = useQuery({
    queryKey: ['annotations', publishedVersion?.id],
    queryFn: () => getAnnotationsByVersion(publishedVersion!.id),
    enabled: !!publishedVersion?.id,
  });

  const createAnnotationMutation = useMutation({
    mutationFn: ({ selector, bodyMd, visibility }: { selector: Selector; bodyMd: string; visibility: 'public' | 'private' }) =>
      createAnnotation(publishedVersion!.id, selector, bodyMd, visibility),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['annotations', publishedVersion?.id] });
      setComposing(false);
      setSelection(null);
      setCapturedSelector(null);
      window.getSelection()?.removeAllRanges();
    },
  });

  // ── Content derivation ───────────────────────────────────────────────────
  const isLegacyHtml = !!id &&
    (publishedVersion?.content_jsonb as { attrs?: { format?: string } } | null)?.attrs?.format === 'html_legacy';

  const sanitizedHtml = useMemo(() => {
    if (!isLegacyHtml || !publishedVersion?.content_jsonb) return '';
    const raw = (publishedVersion.content_jsonb as { content?: [{ attrs?: { html?: string } }] })
      .content?.[0]?.attrs?.html ?? '';
    return sanitizeLegacyHtml(raw);
  }, [isLegacyHtml, publishedVersion?.content_jsonb]);

  const allParas = useMemo(() => {
    if (id && publishedVersion?.content_jsonb && !isLegacyHtml) {
      return extractParagraphs(publishedVersion.content_jsonb as TipTapNode);
    }
    return BOOK_PAGES;
  }, [id, publishedVersion?.content_jsonb, isLegacyHtml]);

  const annotations: Annotation[] = id ? (annotationData?.display ?? []) : mockAnnotations;

  // Track a view once per document per 30-minute window.
  useEffect(() => {
    if (id && publishedVersion) trackDocumentView(id).catch(() => {});
  }, [id, publishedVersion?.id]);

  // ── Measure container ────────────────────────────────────────────────────
  // rAF-debounce prevents a synchronous column reflow on every sub-pixel resize event.
  useEffect(() => {
    const el = contentAreaRef.current;
    if (!el) return;
    let raf = 0;
    const ro = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (entry) {
          setContentWidth(entry.contentRect.width);
          setContentHeight(entry.contentRect.height);
        }
      });
    });
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); };
  }, []);

  // After every content/layout change, measure spread dimensions and page count, then
  // build the anchor → spread index map used by handleLegacyClick.
  //
  // We walk the offsetParent chain rather than using getBoundingClientRect().
  // getBoundingClientRect() on iOS WebKit is unreliable for elements inside a
  // CSS-transformed container, and even on Chrome it can accumulate floating-point
  // error across many columns. offsetLeft values are layout-computed integers
  // relative to the nearest positioned ancestor — transform-independent and
  // consistent across browsers.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || readMode !== 'paged') return;

    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    const step = el.clientWidth + gap;
    spreadStepRef.current = step;
    const measured = Math.max(1, Math.ceil(el.scrollWidth / step));
    totalPagesRef.current = measured;
    setTotalPages(measured);
    setSpreadOffset(s => Math.min(s, measured - 1));

    const map = new Map<string, number>();
    el.querySelectorAll<HTMLElement>('[id], a[name]').forEach(anchor => {
      let target: HTMLElement = anchor;
      // Zero-size <a name="..."> anchors sit at column tail; step to next sibling for correct spread.
      if (anchor.offsetWidth < 1 && anchor.offsetHeight < 1) {
        const next = anchor.nextElementSibling ?? anchor.parentElement?.nextElementSibling;
        if (next instanceof HTMLElement) target = next;
      }
      // Walk the offsetParent chain to accumulate the left offset relative to el.
      // This avoids getBoundingClientRect() transform inconsistencies entirely.
      let left = 0;
      let node: HTMLElement | null = target;
      while (node && node !== el) {
        left += node.offsetLeft;
        node = node.offsetParent as HTMLElement | null;
      }
      const spread = Math.max(0, Math.min(Math.floor(left / step), measured - 1));
      if (anchor.id) map.set(anchor.id, spread);
      const name = anchor.getAttribute('name');
      if (name) map.set(name, spread);
    });
    anchorSpreadMapRef.current = map;
  }, [readMode, sanitizedHtml, allParas, prefs.fontSize, prefs.fontFamily, contentWidth, contentHeight, imagesLoadedCount]);

  // Re-trigger layout measurement when images finish loading. Images cause CSS
  // column reflow, so the anchor map built before load completes is stale.
  // Also drives imagesLoading so the bottom bar can show a "laying out…" state.
  useEffect(() => {
    const el = textRef.current;
    if (!el || readMode !== 'paged') return;
    const imgs = Array.from(el.querySelectorAll<HTMLImageElement>('img'));
    const pending = imgs.filter(img => !img.complete);
    if (pending.length === 0) { setImagesLoading(false); return; }
    setImagesLoading(true);
    let remaining = pending.length;
    const done = () => {
      if (--remaining === 0) {
        setImagesLoading(false);
        setImagesLoadedCount(n => n + 1);
      }
    };
    pending.forEach(img => { img.addEventListener('load', done); img.addEventListener('error', done); });
    return () => { pending.forEach(img => { img.removeEventListener('load', done); img.removeEventListener('error', done); }); };
  }, [sanitizedHtml, readMode]);

  // ── Persistence ──────────────────────────────────────────────────────────
  useEffect(() => {
    const saved = parseInt(localStorage.getItem(pageKey) ?? '0');
    setSpreadOffset(isNaN(saved) ? 0 : saved);
  }, [pageKey]);

  useEffect(() => {
    const t = setTimeout(() => localStorage.setItem(pageKey, String(spreadOffset)), 400);
    return () => clearTimeout(t);
  }, [spreadOffset, pageKey]);

  useEffect(() => { localStorage.setItem('oipub-mode', readMode); }, [readMode]);
  useEffect(() => { localStorage.setItem('oipub-prefs', JSON.stringify(prefs)); }, [prefs]);
  useEffect(() => { if (!sliding) setSliderPage(spreadOffset); }, [spreadOffset, sliding]);

  // ── Text selection detection ─────────────────────────────────────────────
  useEffect(() => {
    const onUp = () => {
      if (composing) return;
      setTimeout(() => {
        const sel = window.getSelection();
        if (sel && sel.toString().trim().length > 4 && textRef.current?.contains(sel.anchorNode)) {
          const rect = sel.getRangeAt(0).getBoundingClientRect();
          setSelection({ text: sel.toString().trim(), rect });
          if (id && textRef.current) setCapturedSelector(captureSelection(textRef.current));
        }
      }, 20);
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.sel-toolbar') && !composing) setSelection(null);
    };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
      document.removeEventListener('mousedown', onDown);
    };
  }, [composing, id]);

  // ── Keyboard navigation ──────────────────────────────────────────────────
  // totalPages read via ref so this effect doesn't re-register on every page turn.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (composing || readMode === 'scroll') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        setSpreadOffset(s => Math.min(s + 1, totalPagesRef.current - 1));
        setActiveAnn(null);
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setSpreadOffset(s => Math.max(s - 1, 0));
        setActiveAnn(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [composing, readMode]);

  // ── Navigation ───────────────────────────────────────────────────────────
  function pageForward() {
    setSpreadOffset(s => Math.min(s + 1, totalPages - 1));
    setActiveAnn(null);
  }

  function pageBackward() {
    setSpreadOffset(s => Math.max(s - 1, 0));
    setActiveAnn(null);
  }

  // Anchor clicks in legacy HTML: O(1) lookup into the pre-computed spread map.
  // No getBoundingClientRect in the hot path — eliminates the CSS column reflow that
  // made every TOC tap slow on long documents.
  const handleLegacyClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const a = (e.target as HTMLElement).closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href?.startsWith('#')) return;
    e.preventDefault();
    const anchorId = decodeURIComponent(href.slice(1));
    if (readMode === 'scroll') { scrollToAnchor(anchorId); return; }
    const spread = anchorSpreadMapRef.current.get(anchorId);
    if (spread !== undefined) setSpreadOffset(spread);
  }, [readMode]);

  // ── Touch swipe ──────────────────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null || readMode === 'scroll') return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 48) { if (dx < 0) pageForward(); else pageBackward(); }
    touchX.current = null;
  };

  // ── Annotations ──────────────────────────────────────────────────────────
  function handleAnnClick(ann: DisplayAnnotation) {
    setActiveAnn(a => (a === ann.id ? null : ann.id));
    setShowAnnPanel(true);
  }

  function saveAnnotation({ note, isPublic }: { note: string; isPublic: boolean }) {
    if (id && publishedVersion && capturedSelector) {
      createAnnotationMutation.mutate({ selector: capturedSelector, bodyMd: note, visibility: isPublic ? 'public' : 'private' });
      return;
    }
    const excerpt = (selection?.text ?? '').slice(0, 120);
    const ann: Annotation = {
      id: 'u' + Date.now(),
      selector: { position: { start: 0, end: 0 }, quote: { exact: excerpt, prefix: '', suffix: '' } },
      excerpt,
      bodyMd: note,
      authorHandle: 'you', authorName: 'You', initials: 'ME', color: '#d97706',
      createdAt: new Date().toISOString(), isPublic,
    };
    setMockAnnotations(prev => [...prev, ann]);
    setComposing(false);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    setActiveAnn(ann.id);
    setShowAnnPanel(true);
  }

  // ── Derived display values ────────────────────────────────────────────────
  const bg = themeBg(prefs.theme);
  const text = themeText(prefs.theme);
  const border = themeBorder(prefs.theme);
  const muted = themeMuted(prefs.theme);
  const ff = fontFamilyValue(prefs.fontFamily);
  const docTitle = docInfo?.title ?? (id ? '...' : 'Life of Emerson');
  const isLoading = !!id && versionLoading;

  return (
    <IonPage>
      <div className="flex h-full flex-col overflow-hidden" style={{ background: bg, color: text }}>
        {/* Top bar */}
        <div className="relative flex shrink-0 items-center justify-between px-4 py-1.5" style={{ background: bg, borderBottom: `1px solid ${border}` }}>
          <button onClick={() => history.goBack()} aria-label="Back"
            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-black/5" style={{ color: text }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>

          <div className="flex flex-1 items-center justify-center gap-2.5">
            <Logo size="sm" />
            {id && docInfo && (
              <span className="hidden font-serif text-[0.78rem] italic sm:block" style={{ color: muted }}>
                {docInfo.title}
              </span>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            <button onClick={() => setShowSettings(s => !s)} aria-label="Reading settings"
              className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${showSettings ? 'bg-blue-100 text-blue-700' : 'hover:bg-black/5'}`} style={{ color: showSettings ? undefined : text }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
            <button onClick={() => setBookmarked(b => !b)} aria-label="Bookmark"
              className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-black/5" style={{ color: text }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill={bookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
            <button
              onClick={() => setShowAnnPanel(s => !s)}
              aria-label="Annotations"
              className={`relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${showAnnPanel ? 'bg-blue-100 text-[#1a56a0]' : 'hover:bg-black/5'}`}
              style={{ color: showAnnPanel ? undefined : text }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              {annotations.length > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#1a56a0] text-[0.58rem] font-bold text-white">
                  {annotations.length}
                </span>
              )}
            </button>
          </div>

          {showSettings && (
            <ReaderSettings prefs={prefs} onChange={p => setPrefs(prev => ({ ...prev, ...p }))} onClose={() => setShowSettings(false)} />
          )}
        </div>

        {/* Running header */}
        <div className="flex shrink-0 items-center justify-between px-8 py-1.5" style={{ borderBottom: `1px solid ${border}` }}>
          <span className="text-[0.63rem] font-extrabold uppercase tracking-widest" style={{ color: muted }}>
            {docTitle.toUpperCase()}
          </span>
          <span className="font-serif text-[0.72rem]" style={{ color: muted }}>[{spreadOffset + 1}]</span>
        </div>

        {/* Content */}
        <div
          ref={contentAreaRef}
          className={`relative flex-1 ${readMode === 'scroll' ? 'overflow-y-auto' : 'overflow-hidden'}`}
          onTouchStart={readMode === 'paged' ? onTouchStart : undefined}
          onTouchEnd={readMode === 'paged' ? onTouchEnd : undefined}
          onClick={readMode === 'paged' && !composing && !isLoading ? (e: React.MouseEvent<HTMLDivElement>) => {
            if ((e.target as HTMLElement).closest('a, button, input, select, textarea')) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            if (x < rect.width * 0.33) pageBackward();
            else if (x > rect.width * 0.67) pageForward();
          } : undefined}
        >
          <div
            className={`${readMode === 'scroll' ? 'min-h-full' : 'h-full overflow-hidden'} px-10 py-8 sm:px-6`}
            style={{ background: bg }}
          >
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
              </div>
            ) : id && !publishedVersion && !versionLoading ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-center" style={{ color: muted }}>
                <p className="text-sm">This document hasn't been published yet.</p>
                <button
                  onClick={() => history.goBack()}
                  className="rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:bg-black/5"
                  style={{ borderColor: border }}
                >
                  Go back
                </button>
              </div>
            ) : (
              <div className={`mx-auto flex max-w-5xl gap-8 ${readMode === 'scroll' ? 'min-h-full' : 'h-full'}`}>
                <div style={{ flex: 1, position: 'relative', ...(readMode === 'paged' ? { height: '100%', overflow: 'hidden' } : {}) }}>
                  <div
                    ref={textRef}
                    style={{
                      height: readMode === 'paged' ? '100%' : undefined,
                      transform: readMode === 'paged' && spreadOffset > 0
                        ? `translateX(${-spreadOffset * spreadStepRef.current}px)`
                        : undefined,
                      transition: readMode === 'paged' ? 'transform 0.25s ease' : undefined,
                      willChange: readMode === 'paged' ? 'transform' : undefined,
                      columnCount: readMode === 'scroll' ? 1 : 2,
                      columnFill: readMode === 'paged' ? 'auto' : undefined,
                      columnGap: '3.5rem',
                      columnRule: readMode === 'scroll' ? 'none' : `1px solid ${border}`,
                      fontSize: `${prefs.fontSize}px`,
                      lineHeight: 1.78,
                      textAlign: 'justify',
                      hyphens: 'auto',
                      fontFamily: ff,
                      color: text,
                    } as React.CSSProperties}
                    className="max-lg:!columns-1 max-lg:[column-rule:none] [&_h1]:!text-left [&_h2]:!text-left [&_h3]:!text-left [&_h4]:!text-left [&_h5]:!text-left [&_h6]:!text-left [&_caption]:!text-left [&_figcaption]:!text-left"
                  >
                    {isLegacyHtml ? (
                      <div onClick={handleLegacyClick}>
                        <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
                      </div>
                    ) : (
                      <div>
                        {allParas.map((para, i) => (
                          <div key={i}>
                            {renderPageContent(para, annotations, handleAnnClick, activeAnn)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {readMode === 'scroll' && (
                  <MarginNoteRail annotations={annotations} activeId={activeAnn} onAnnotationClick={handleAnnClick} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex shrink-0 items-center gap-1.5 px-3 py-2" style={{ background: bg, borderTop: `1px solid ${border}` }}>
          <button
            onClick={() => setSpreadOffset(0)}
            disabled={spreadOffset === 0 || readMode === 'scroll'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 disabled:opacity-25"
            style={{ color: text }}
            title="First page"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/><line x1="5" y1="6" x2="5" y2="18"/></svg>
          </button>

          <button
            onClick={pageBackward}
            disabled={spreadOffset === 0 || readMode === 'scroll'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 disabled:opacity-25"
            style={{ color: text }}
            title="Previous page"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>

          <div className="flex flex-1 flex-col items-center gap-0.5">
            {imagesLoading && readMode === 'paged' ? (
              <div className="flex items-center gap-2" style={{ color: muted }}>
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span className="text-[0.7rem]">Laying out pages…</span>
              </div>
            ) : (
              <>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, totalPages - 1)}
                  value={sliderPage}
                  disabled={readMode === 'scroll' || totalPages <= 1}
                  onChange={e => { setSliding(true); setSliderPage(Number(e.target.value)); }}
                  onPointerUp={e => { setSpreadOffset(Number((e.target as HTMLInputElement).value)); setSliding(false); }}
                  className="w-full max-w-[220px] cursor-pointer accent-[#2f3e46] disabled:opacity-40"
                  aria-label="Page"
                />
                <span className="text-[0.7rem] tabular-nums transition-colors" style={{ color: sliding ? text : muted }}>
                  {sliding ? `→ ${sliderPage + 1} / ${totalPages}` : `Page ${spreadOffset + 1} of ${totalPages}`}
                </span>
              </>
            )}
          </div>

          <button
            onClick={pageForward}
            disabled={spreadOffset >= totalPages - 1 || readMode === 'scroll'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 disabled:opacity-25"
            style={{ color: text }}
            title="Next page"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>

          <button
            onClick={() => setSpreadOffset(totalPages - 1)}
            disabled={spreadOffset >= totalPages - 1 || readMode === 'scroll'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-black/5 disabled:opacity-25"
            style={{ color: text }}
            title="Last page"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/><line x1="19" y1="6" x2="19" y2="18"/></svg>
          </button>

          <button
            onClick={() => setReadMode(m => m === 'paged' ? 'scroll' : 'paged')}
            title={readMode === 'paged' ? 'Switch to scroll' : 'Switch to book pages'}
            className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-black/5"
            style={{ color: text, borderColor: border }}
          >
            {readMode === 'paged'
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="18" rx="1"/><rect x="13" y="3" width="8" height="18" rx="1"/></svg>
            }
          </button>
        </div>

        {/* Annotation panel overlay */}
        {showAnnPanel && (
          <div className="absolute inset-0 z-40 flex justify-end bg-black/25" onClick={() => setShowAnnPanel(false)}>
            <div onClick={e => e.stopPropagation()} className="h-full">
              <AnnotationPanel
                annotations={annotations}
                onClose={() => setShowAnnPanel(false)}
                onAnnotationClick={ann => setActiveAnn(a => (a === ann.id ? null : ann.id))}
                activeId={activeAnn}
                filter={annFilter}
                onFilterChange={setAnnFilter}
              />
            </div>
          </div>
        )}

        {/* Selection toolbar */}
        {selection && !composing && (
          <SelectionToolbar
            selection={selection}
            onAnnotate={() => {
              if (status !== 'authenticated') { history.push('/auth'); return; }
              setComposing(true);
            }}
            onDismiss={() => { setSelection(null); setCapturedSelector(null); window.getSelection()?.removeAllRanges(); }}
          />
        )}

        {/* Compose sheet */}
        {composing && selection && (
          <AnnotationCompose
            selection={selection}
            onSave={saveAnnotation}
            onCancel={() => { setComposing(false); setSelection(null); setCapturedSelector(null); window.getSelection()?.removeAllRanges(); }}
            theme={prefs.theme}
          />
        )}
      </div>
    </IonPage>
  );
}
